<template>
  <div class="admin-accounts">
      <div class="accounts-header-actions">
        <div class="search-box">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            v-model="searchQuery"
            @input="handleSearch"
            placeholder="Search by business, email, or phone..."
          />
        </div>

        <div class="accounts-filters">
          <div class="filter-box">
            <select v-model="statusFilter" @change="handleFilterChange">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="trial">In Trial</option>
              <option value="canceled">Canceled</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div class="sort-box">
            <select v-model="sortBy" @change="handleSortChange">
              <option value="last_active">Sort by Last Active</option>
              <option value="created_at">Sort by Joined Date</option>
            </select>
          </div>
          <button type="button" class="add-account-btn" @click="showAddModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Create account
          </button>
        </div>
      </div>

      <div class="account-stats-grid">
        <div class="account-stat-card total">
          <span class="stat-label">Total Accounts</span>
          <strong class="stat-value">{{ stats.total }}</strong>
        </div>
        <div class="account-stat-card active">
          <span class="stat-label">Active</span>
          <strong class="stat-value">{{ stats.active }}</strong>
        </div>
        <div class="account-stat-card canceled">
          <span class="stat-label">Canceled</span>
          <strong class="stat-value">{{ stats.canceled }}</strong>
        </div>
        <div class="account-stat-card trial">
          <span class="stat-label">In Trial</span>
          <strong class="stat-value">{{ stats.trial }}</strong>
        </div>
      </div>

    <div class="page-table-container">
      <div>
        
        <div class="table-responsive">
          <table class="admin-table mb-0 small">
            <thead>
              <tr>
                <th>Business</th>
                <th>Owner</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Users</th>
                <th>Joined</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in accounts" :key="account.id">
                <td>
                  <div class="biz-name">{{ account.name }}</div>
                </td>
                <td>
                  <div class="owner-email">{{ account.owner_email || account.email }}</div>
                  <div class="owner-name">{{ account.owner_name }}</div>
                </td>
                <td class="phone-cell">{{ account.owner_phone || account.phone || '-' }}</td>
                <td>
                  <span class="status-badge" :class="account.status || 'null'">
                    {{ account.status || 'NULL' }}
                  </span>
                </td>
                <td>{{ account.users_count || 0 }}</td>
                <td>{{ formatDate(account.created_at) }}</td>
                <td>{{ account.owner_last_active && new Date(account.owner_last_active).getTime() > 0 ? formatDateTime(account.owner_last_active) : 'Never' }}</td>
                <td>
                  <button class="action-btn view-btn" @click="$router.push(`/admin/accounts/${account.id}`)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    View
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <table-loading-bar v-if="loading" class="page-table-state loading-state" />
        <div v-else-if="!accounts.length" class="page-table-state text-muted">No accounts found matching your criteria.</div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="pagination.totalPages > 1">
      <button 
        class="page-btn" 
        :disabled="pagination.page <= 1"
        @click="changePage(pagination.page - 1)"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      
      <span class="page-info">
        Page {{ pagination.page }} of {{ pagination.totalPages }}
      </span>

      <button 
        class="page-btn" 
        :disabled="pagination.page >= pagination.totalPages"
        @click="changePage(pagination.page + 1)"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>

    <!-- Add Account Modal -->
    <div v-if="showAddModal" class="admin-modal-overlay" @click.self="closeAddModal">
      <div class="admin-modal">
        <div class="admin-modal-header">
          <h3>Create New Account</h3>
          <button class="close-modal" @click="closeAddModal">&times;</button>
        </div>
        <form @submit.prevent="createAccount" class="admin-modal-body">
          <div class="form-group">
            <label>Business Name</label>
            <input type="text" v-model="addForm.business_name" required placeholder="My Store LLC" />
          </div>
          <div class="form-group">
            <label>Owner Name</label>
            <input type="text" v-model="addForm.owner_name" required placeholder="John Doe" />
          </div>
          <div class="form-group">
            <label>Email Address</label>
            <input type="email" v-model="addForm.email" required placeholder="john@example.com" />
          </div>
          <div class="form-group">
            <label>Initial Password</label>
            <input type="password" v-model="addForm.password" required placeholder="Min. 6 characters" />
          </div>
          <div v-if="formError" class="modal-error">{{ formError }}</div>
          
          <div class="admin-modal-footer">
            <button type="button" class="btn-cancel" @click="closeAddModal">Cancel</button>
            <button type="submit" class="btn-submit" :disabled="formLoading">
              <span v-if="!formLoading">Create Account</span>
              <span v-else class="admin-spinner small"></span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'AdminAccounts',
  persistedFilterState: { key: 'admin-accounts', fields: ['searchQuery', 'statusFilter', 'sortBy'] },
  data() {
    return {
      accounts: [],
      searchQuery: '',
      statusFilter: '',
      sortBy: 'last_active',
      loading: true,
      searchTimeout: null,
      stats: {
        total: 0,
        active: 0,
        canceled: 0,
        trial: 0
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1
      },
      showAddModal: false,
      formLoading: false,
      formError: '',
      addForm: {
        business_name: '',
        owner_name: '',
        email: '',
        password: ''
      }
    }
  },
  created() {
    this.fetchAccounts()
  },
  methods: {
    async fetchAccounts() {
      this.loading = true
      try {
        const res = await axios.get(`${process.env.VUE_APP_API}/admin/accounts`, {
          params: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            keyword: this.searchQuery,
            status: this.statusFilter,
            sortBy: this.sortBy
          }
        })
        if (res.data.success) {
          this.accounts = res.data.data
          this.pagination = res.data.pagination
          this.stats = res.data.stats || this.stats
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.$router.push('/admin/login')
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1
        this.fetchAccounts()
      }, 400) // Debounce search
    },
    handleSortChange() {
      this.pagination.page = 1;
      this.fetchAccounts();
    },
    handleFilterChange() {
      this.pagination.page = 1;
      this.fetchAccounts();
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.page = page
        this.fetchAccounts()
      }
    },
    closeAddModal() {
      this.showAddModal = false;
      this.formError = '';
      this.addForm = { business_name: '', owner_name: '', email: '', password: '' };
    },
    async createAccount() {
      this.formLoading = true;
      this.formError = '';
      try {
        const res = await axios.post(`${process.env.VUE_APP_API}/admin/accounts`, this.addForm);
        if (res.data.success) {
          this.closeAddModal();
          this.fetchAccounts();
          alert('Account created successfully!');
        }
      } catch (error) {
        this.formError = error.response?.data?.message || 'Failed to create account.';
      } finally {
        this.formLoading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('MMM D, YYYY')
    },
    formatDateTime(date) {
      if (!date) return 'Never'
      return moment(date).fromNow()
    }
  }
}
</script>

<style scoped>
.table-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-responsive, .invoice-builder-table {
  background-color: transparent !important;
}
.admin-accounts {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  max-width: 100%;
}

.accounts-header {
  display: flex;
  justify-content: flex-end;
}

.accounts-header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.accounts-filters {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}

.account-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.account-stat-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 104px;
  padding: 20px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.account-stat-card::before {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  content: '';
  background: #6366f1;
}

.account-stat-card.active::before { background: #10b981; }
.account-stat-card.canceled::before { background: #ef4444; }
.account-stat-card.trial::before { background: #3b82f6; }

.stat-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.stat-value {
  color: #111827;
  font-size: 30px;
  line-height: 1;
}

.add-account-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

.add-account-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
}

.sort-box select,
.filter-box select {
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 12px top 50%;
  background-size: 10px auto;
  padding-right: 32px;
}

.sort-box select:focus,
.filter-box select:focus {
  border-color: #6366f1;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.sort-box select option,
.filter-box select option {
  background-color: #ffffff;
  color: #111827;
}

.search-box {
  position: relative;
  width: 300px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.search-box input {
  width: 100%;
  padding: 10px 14px 10px 38px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #111827;
  font-size: 14px;
  transition: all 0.2s;
  outline: none;
}

.search-box input:focus {
  border-color: #6366f1;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}


.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
  gap: 16px;
}

.admin-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
}

.admin-table th {
  text-align: left;
  padding: 14px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}

.admin-table td {
  padding: 16px 20px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  white-space: nowrap;
}

.admin-table tr:hover td {
  background: #f8fafc;
}

.biz-name {
  font-weight: 600;
  color: #111827;
}

.owner-email {
  font-weight: 500;
}

.owner-name {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.plan-badge {
  padding: 4px 8px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #818cf8;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.active { background: rgba(16, 185, 129, 0.15); color: #34d399; }
.status-badge.trial, .status-badge.on_trial { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
.status-badge.suspended { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
.status-badge.canceled, .status-badge.expired, .status-badge.inactive { background: rgba(239, 68, 68, 0.15); color: #f87171; }
.status-badge.null, .status-badge.unknown { background: rgba(148, 163, 184, 0.15); color: #94a3b8; }

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #4f46e5;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.page-info {
  font-size: 13px;
  color: #64748b;
}

.page-btn {
  background: #ffffff;
  border: 1px solid #dbe3ef;
  color: #334155;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #f8fafc;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-center { text-align: center; }
.text-muted { color: #64748b; }

/* Modal Styles */
.admin-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.admin-modal {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}

.admin-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.admin-modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.close-modal {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
}

.close-modal:hover {
  color: #111827;
}

.admin-modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

.form-group input {
  padding: 12px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #111827;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.form-group input:focus {
  border-color: #6366f1;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.modal-error {
  padding: 10px;
  background: rgba(239, 68, 68, 0.1);
  color: #fca5a5;
  border-radius: 8px;
  font-size: 13px;
}

.admin-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.btn-cancel {
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f8fafc;
}

.btn-submit {
  padding: 10px 20px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.admin-spinner.small {
  width: 18px;
  height: 18px;
  border-width: 2px;
}

@media (max-width: 600px) {
  .account-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .accounts-header-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .accounts-filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-left: 0;
  }
  
  .search-box {
    width: 100%;
  }

  .sort-box select,
  .filter-box select {
    width: 100%;
  }

  .add-account-btn {
    width: 100%;
    justify-content: center;
    grid-column: 1 / -1;
  }

  .admin-table th, .admin-table td {
    padding: 12px 14px;
    font-size: 13px;
  }


  .pagination {
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .accounts-header-actions {
    gap: 8px;
  }
  
  .search-box input, .sort-box select, .filter-box select {
    font-size: 16px; /* Prevents auto-zoom on iOS */
  }
  .search-box input {
    padding: 12px 14px 12px 40px;
  }
  .sort-box select,
  .filter-box select {
    padding: 12px 14px;
  }
}
</style>
